/* @flow */

import React, { useContext } from "react";
import { useData } from "crustate/react";
import { useBrowser } from "@awardit/react-use-browser";
import { useUi, MODE } from "helpers/ui";
import { HomeData, CmsBlockData } from "state/data";
import { StoreInfoContext } from "entrypoint/shared";
import { Helmet } from "react-helmet-async";
import { useTranslate } from "@awardit/react-use-translate";
import { formatMeta } from "helpers/get-meta";
import { Button, ButtonLink, Wrapper, PromotionBlock, PromotionWrapper } from "@crossroads/ui-components";
import CategorySection from "components/CategorySection";
import Loading from "pages/Loading";
import { Offerings } from "@crossroads/shop-views";
import useBrowserDimensions from "helpers/hooks/use-browser-dimensions";
import ProductCard from "components/ProductCard";
import HeroFullWidth from "components/HeroFullWidth";
import Arrow from "icons/arrow.svg";

import styles from "./styles.scss";

const enrichImagesData = (data: any) => {
  return data.map(s => ({
    ...s,
    images: [
      { ...s.images.large, media: `screen` },
    ],
  }));
};

const Home = (): React$Node => {
  const browser = useBrowser();
  const t = useTranslate();
  const home = useData(HomeData);
  const offerings = useData(CmsBlockData);
  const { info } = useContext(StoreInfoContext);
  const { isDesktop } = useUi();
  const { width: browserWidth } = useBrowserDimensions();
  const {
    setHamburgerOpen,
    setSubNavOpen,
    setViewMode,
    viewMode,
  } = useUi();

  const offeringsContent = offerings.state === "LOADED" && offerings.data ?
    offerings.data.content : null;

  if (home.state !== "LOADED") {
    return <Loading />;
  }

  const meta = formatMeta({
    title: info.defaultTitle,
    description: info.defaultDescription,
  });

  const getPopularProductAmount = () => {
    if (browserWidth < Number.parseInt(styles.tiny, 10)) {
      return 2;
    }

    if (browserWidth < Number.parseInt(styles.medium, 10)) {
      return 3;
    }

    return 4;
  };

  const heroData =
  home.data.hero.map(c => ({
    heading: c.block.heading,
    description: c.block.text,
    url: c.block.link,
    images: {
      large: {
        url: c.block.largeImage && c.block.largeImage.x1,
      },
    },
    button: {
      text: c.block.cta,
      link: c.block.link,
    },
  }));

  return (
    <div className={styles.home}>
      <Helmet
        title={meta.title}
        meta={meta.data}
      />

      {heroData && heroData.length > 0 &&
        <HeroFullWidth data={enrichImagesData(heroData)[0]} />
      }

      <Wrapper>
        <div className={styles.popularCategories}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t("HOMEVIEW.POPULAR_CATEGORIES")}</h2>
            <Button
              type="button"
              aria-controls={isDesktop ? "desktopCategories" : "categoriesMobile"}
              aria-expanded={viewMode === MODE.CATEGORIES ?? "false"}
              variant="blank"
              className={styles.cta}
              onClick={() => {
                setHamburgerOpen(true);
                setSubNavOpen(true);
                setViewMode(MODE.CATEGORIES);
              }}
            >
              {t("HOMEVIEW.ALL_CATEGORIES")}
              <Arrow aria-hidden="true" />
            </Button>
          </div>

          <CategorySection />
        </div>

        {home.data.popularProducts && home.data.popularProducts.items.length >= 4 && (
          <div className={styles.popularProducts}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t("POPULAR.POPULAR_PRODUCTS")}</h2>
              <ButtonLink
                className={styles.cta}
                variant="blank"
                to="/popular-products"
              >
                {t("HOMEVIEW.MORE_POPULAR_PRODUCTS")}
                <Arrow aria-hidden="true" />
              </ButtonLink>
            </div>
            <div className={styles.products}>
              {browser && home.data.popularProducts.items
                .slice(0, getPopularProductAmount())
                .map((p, i) => (
                  <div key={p.name} className={styles.product}>
                    <ProductCard product={p} position={i} list={t("POPULAR.POPULAR_PRODUCTS")} />
                  </div>
                ))}
            </div>
          </div>
        )}

        {offeringsContent && (
          <div className={styles.offeringsSection}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>{t("HOMEVIEW.OFFERINGS")}</h2>
            </div>
            <Offerings items={offeringsContent} />
          </div>
        )}

        {home.data.block2FullWidth && (
          <PromotionWrapper variant="full">
            <PromotionBlock {...home.data.block2FullWidth.block} variant="full" btnVariant="primary" />
          </PromotionWrapper>
        )}

        {(home.data.block1Left && home.data.block1Right) && (
          <PromotionWrapper variant="double">
            <PromotionBlock {...home.data.block1Left.block} variant="double" />
            <PromotionBlock {...home.data.block1Right.block} variant="double" />
          </PromotionWrapper>
        )}
      </Wrapper>
    </div>
  );
};

export default Home;
